import * as React from "react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
    Avatar,
    BoxTypeSelectionResult,
    classNames,
    EmailKeyboardLayout,
    EnglishKeyboardLayout,
    ErrorMessageDialog,
    GermanKeyboardLayout,
    Keyboard,
    KeyboardLayout,
    layout,
    List,
    ListItem,
    ListItemDescription,
    ListItemIcon,
    ListItemName,
    ListItemText,
    MaterialIcon,
    PhoneKeyboardLayout,
    PushButton,
    TabBar,
    TabItem,
    TextInput,
    useAsyncCallback,
    useBoxes,
    useOccupancies,
    usePlatform,
    useStorageItemValue,
    ViewContent,
    ViewFooter
} from "@variocube/cube-ui";
import { stylesheet } from "typestyle";
import {
    Delivery,
    DeliveryState,
    filterRecipients,
    getOccupancyDeliveries,
    Recipient,
    Tenant,
    TenantUser,
    UseExistingBox
} from "./types";
import { StringUtils } from "@variocube/app-common";
import { AccessCodeAlphabet, Occupancy } from "@variocube/app-api";
import { logger } from "./logger";
import { SizeSelectionView } from "./SizeSelectionView";
import { useLocalization } from "../i18n";
import { StorageBoxAccess } from "./StorageBoxAccess";
import { AddDeliveryToBoxView } from "./AddDeliveryToBoxView";
import { compareRecipients } from "./compareRecipients";
import { v4 as uuidv4 } from 'uuid';

interface CreateDeliveryViewProps {
    recipients: Recipient[];
    onDone: (occupancy: Occupancy) => void;
    user: TenantUser;
}

enum CreateDeliveryStep {
    RecipientSelection,
    Details,
    BoxSelection
}

const log = logger.sub("CreateDeliveryView");

export function CreateDeliveryView({ recipients, onDone, user }: CreateDeliveryViewProps) {
    const { t, language } = useLocalization();
    const platform = usePlatform();
    const boxes = useBoxes();
    const [searchTab, setSearchTab] = useState<boolean>(true);
    const [text, setText] = useState("");
    const [recipient, setRecipient] = useState<Recipient>();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [deliveryNote, setDeliveryNote] = useState('');
    const [focus, setFocus] = useState<'recipient' | 'name' | 'email' | 'phone'>('recipient');
    const [step, setStep] = useState<CreateDeliveryStep>(CreateDeliveryStep.RecipientSelection);
    const [occupancy, setOccupancy] = useState<Occupancy>();
    const [error, setError] = useState<{ title: string, description: string }>();

    const tenant = useStorageItemValue<Tenant>("tenant");
    const useExistingBox = tenant?.useExistingBox ?? UseExistingBox.AskToAdd;

    const [separateBox, setSeparateBox] = useState(false);

    const allOccupancies = useOccupancies();

    const existingOccupanciesForRecipients = useMemo(() => {
        if (recipient) {
            const r: Recipient[] = [recipient];
            return allOccupancies.filter(occupancy => {
                const occupancyDeliveries = getOccupancyDeliveries(occupancy);
                if (occupancyDeliveries) {
                    return occupancyDeliveries.every(occupancyDelivery =>
                        compareRecipients(r, occupancyDelivery.recipients)
                    );
                }
            });
        } else {
            return [];
        }
    }, [tenant, recipient, allOccupancies])

    useEffect(() => {
        if (UseExistingBox.Disabled === useExistingBox) {
            setSeparateBox(true);
        }
        else if (UseExistingBox.Always === useExistingBox) {
            handleAddDeliveryToBox();
        }
        else if (UseExistingBox.AskToAdd === useExistingBox) {
            setSeparateBox(existingOccupanciesForRecipients.length === 0);
        }
    }, [useExistingBox, existingOccupanciesForRecipients]);

    const handleAddDeliveryToBox = useCallback(() => {
        if (existingOccupanciesForRecipients.length > 0) {
            setSeparateBox(false);
            const occ = existingOccupanciesForRecipients[0];
            setOccupancy(occ);
        }
    }, [deliveryNote, recipient, existingOccupanciesForRecipients]);

    function handleFocus(field: typeof focus) {
        setFocus(field);
        if (field !== 'recipient') setText('');
    }

    function onKeyPress(key: string) {
        if (step === CreateDeliveryStep.RecipientSelection) {
            switch (focus) {
                case "recipient": setText(text + key); break;
                case "email": setEmail(email + key); break;
                case "phone": setPhone(phone + key); break;
                case "name": setName(name + key); break;
            }
        }
        if (step === CreateDeliveryStep.Details) {
            setDeliveryNote(deliveryNote + key);
        }
    }

    function onRecipientSelect(recipient: Recipient) {
        setRecipient(recipient);
        setStep(CreateDeliveryStep.Details);
    }

    async function handleNext() {
        switch (step) {
            case CreateDeliveryStep.RecipientSelection:
                setStep(CreateDeliveryStep.Details);
                break;
            case CreateDeliveryStep.Details:
                setStep(CreateDeliveryStep.BoxSelection);
                break;
            case CreateDeliveryStep.BoxSelection:
                break;
        }
    }

    function handleBack() {
        switch (step) {
            case CreateDeliveryStep.BoxSelection:
                setStep(CreateDeliveryStep.Details);
                setSeparateBox(useExistingBox === UseExistingBox.Disabled)
                break;
            case CreateDeliveryStep.Details:
                setStep(CreateDeliveryStep.RecipientSelection)
                break;
        }
    }

    //after user selects a size 
    const occupy = useAsyncCallback(async (typeSelectionResult: BoxTypeSelectionResult) => {
        try {
            // Occupy box and show delivery flow
            const delivery: Delivery = {
                delivery: {
                    deliveryId: uuidv4(),
                    isCreatedOnCube: true,
                    state: DeliveryState.Stored,
                    storedBy: user.username
                },
                recipients: [recipient ? recipient : { recipientName: name, email, phone }],
                order: {
                    deliveryNoteId: deliveryNote,
                    orderId: "",
                    description: "",
                },
                storage: {},
            }
            const occRequest = {
                ...typeSelectionResult,
                content: {
                    deliveries: [delivery]
                },
                accessCodeShape: {
                    alphabet: AccessCodeAlphabet.OneToNine,
                    length: 5
                },
            };
            const occupancy = await platform.occupy(occRequest)
            log.info('Occupancy created', occupancy);
            setOccupancy(occupancy);
        } catch (err: any) {
            if (err && err.message === 'Occupy failed') {
                setError({ title: t("InsufficientStorage.Title"), description: t("InsufficientStorage.Description") });
            }
        }
    }, [platform, recipient, deliveryNote])

    const cancel = useAsyncCallback(async () => {
        if (occupancy) {
            await platform.cancelOccupancy(occupancy.uuid);
            setOccupancy(undefined)
        }
    }, [occupancy, platform]);

    //The user confirms that all goods are in the cube box 
    const confirm = useAsyncCallback(async () => {
        if (occupancy === undefined) {
            throw new Error('Occupancy not found.');
        }
        log.info('use seperate box', separateBox);
        if (separateBox) {
            await platform.confirmOccupancy(occupancy.uuid);
            onDone(occupancy);
        } else {
            const delivery: Delivery = {
                delivery: {
                    deliveryId: uuidv4(),
                    isCreatedOnCube: true,
                    state: DeliveryState.Stored,
                    storedBy: user.username
                },
                recipients: [recipient ? recipient : { recipientName: name, email, phone }],
                order: {
                    deliveryNoteId: deliveryNote,
                    orderId: "",
                    description: "",
                },
                storage: {},
            }
            log.info("Adding new Delivery to existing occupancy", occupancy);
            const content = {
                ...occupancy.content,
                deliveries: [
                    ...(occupancy.content.deliveries ?? []),
                    delivery
                ]
            }
            await platform.updateOccupancy(occupancy.uuid, content);
            onDone(occupancy);
        }
    }, [occupancy, separateBox]);


    const recipientOptions = useMemo<Recipient[]>(() => {
        return filterRecipients(recipients, text);
    }, [text, recipients]);

    const canNext = useMemo(() => {
        switch (step) {
            case CreateDeliveryStep.RecipientSelection:
                return !!name && !!email;
            case CreateDeliveryStep.Details:
                return !!deliveryNote;
            default:
                return true;
        }
    }, [step, name, email, phone, deliveryNote]);

    const occupiedBoxes = useMemo(() => {
        const results = (occupancy) ?
            boxes.filter(b => b.number === occupancy.boxNumber) :
            [];
        return results;
    }, [boxes, occupancy]);

    const keyboardLayout = useMemo<KeyboardLayout>(() => {
        switch (focus) {
            case "email":
                return EmailKeyboardLayout;
            case "phone":
                return PhoneKeyboardLayout;
            default:
                return language == "de" ? GermanKeyboardLayout : EnglishKeyboardLayout;
        }
    }, [focus, language]);

    return (
        <Fragment>
            <ViewContent>
                {step == CreateDeliveryStep.RecipientSelection && (
                    <Fragment>
                        <div className={classNames(layout.fixed, layout.horizontal, layout.spaceBetween)}>
                            <TabBar>
                                <TabItem active={searchTab} onClick={() => setSearchTab(true)}>
                                    {t("SelectRecipient")}
                                </TabItem>
                                <TabItem active={!searchTab} onClick={() => setSearchTab(false)}>
                                    {t("EnterRecipientDetails")}
                                </TabItem>
                            </TabBar>
                        </div>
                        <div className={styles.recipientSelection}>
                            {searchTab &&
                                <div className={classNames(styles.contentBox)}>
                                    {recipientOptions.length > 0 && (
                                        <List>
                                            {recipientOptions.map((r, i) => (
                                                <RecipientItem
                                                    key={'recipient-' + i}
                                                    recipient={r}
                                                    onSelect={onRecipientSelect}
                                                />
                                            ))}
                                        </List>
                                    )}
                                    {(recipientOptions.length === 0 && text.length >= 3) && (
                                        <p className={styles.promptText}>{t("NoRecipientFound")} {text}</p>
                                    )}
                                    {(recipientOptions.length === 0 && text.length < 3 && (
                                        <p className={styles.promptText}>{t("SelectRecipientPrompt")}</p>
                                    ))}
                                </div>
                            }
                            {!searchTab &&
                                <div className={classNames(layout.vertical, layout.spaceBetween)}>
                                    <TextInput
                                        onChange={setName}
                                        onFocus={() => handleFocus('name')}
                                        value={name}
                                        placeholder={t("Name")}
                                        className={classNames(layout.fill, styles.textInput)}
                                    />
                                    <div className={classNames(layout.horizontal, layout.spaceBetween)}>
                                        <TextInput
                                            onChange={setEmail}
                                            onFocus={() => handleFocus('email')}
                                            value={email}
                                            placeholder={t("Email")}
                                            className={classNames(layout.fill, styles.textInput)}
                                        />
                                        <TextInput
                                            onChange={setPhone}
                                            onFocus={() => handleFocus('phone')}
                                            value={phone}
                                            placeholder={t("Phone")}
                                            className={classNames(layout.fill, styles.textInput)}
                                        />
                                    </div>
                                </div>
                            }
                        </div>
                    </Fragment>
                )}

                {step == CreateDeliveryStep.Details && (
                    <TextInput
                        onChange={setDeliveryNote}
                        onFocus={() => handleFocus('recipient')}
                        value={deliveryNote}
                        placeholder={t("DeliveryNoteId")}
                        className={classNames(layout.fill, styles.textInput)}
                    />
                )}

                {step == CreateDeliveryStep.BoxSelection && (
                    <Fragment>
                        {!occupancy && !separateBox && existingOccupanciesForRecipients.length > 0 &&
                            <AddDeliveryToBoxView onHandleAddDeliveryToBox={handleAddDeliveryToBox} onSeparateBox={() => setSeparateBox(true)} />
                        }
                        {!occupancy && (
                            <SizeSelectionView onBack={handleBack} onSelect={occupy.execute} />
                        )}
                        {occupancy && (
                            <StorageBoxAccess boxes={occupiedBoxes} onCancel={cancel.execute} onConfirm={confirm.execute} />
                        )}
                    </Fragment>
                )}
            </ViewContent>

            {step != CreateDeliveryStep.BoxSelection && (
                <ViewFooter className={layout.center}>
                    <div className={classNames(styles.control)}>
                        {(searchTab && step == CreateDeliveryStep.RecipientSelection) && (
                            <TextInput
                                onChange={setText}
                                value={text}
                                onFocus={() => handleFocus('recipient')}
                                placeholder={t("SearchRecipient")}
                                className={classNames(layout.fill, styles.textInput)}
                            />
                        )}
                        <div className={classNames(layout.horizontal)}>
                            <Keyboard
                                className={classNames(styles.keyboard)}
                                onType={onKeyPress}
                                border={true}
                                layout={keyboardLayout}
                            />
                            <div className={classNames(styles.keyboard, styles.navigation)}>
                                <div className={classNames(layout.vertical, layout.spaceBetween)}>
                                    <div className={classNames(layout.horizontal, layout.center, styles.keyboardButtonContainer)}>
                                        <PushButton
                                            onClick={handleBack}
                                            disabled={step === CreateDeliveryStep.RecipientSelection}
                                        >
                                            {t("Back")}
                                        </PushButton>
                                    </div>
                                    <div className={classNames(layout.horizontal, layout.center, styles.keyboardButtonContainer)}>
                                        <PushButton
                                            onClick={handleNext}
                                            disabled={!canNext}
                                        >
                                            {t("Next")}
                                        </PushButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ViewFooter>
            )}
            {error && <ErrorMessageDialog title={error.title} description={error.description} onClose={() => { setError(undefined) }} />}

        </Fragment>
    )
}

interface RecipientItemProps {
    recipient: Recipient;
    onSelect: (recipient: Recipient) => void;
}
function RecipientItem({ recipient, onSelect }: RecipientItemProps) {
    const handleClick = useCallback(() => onSelect(recipient), [onSelect, recipient]);
    const abbr = StringUtils.abbreviateInitials(recipient.recipientName, 3);
    const description = useMemo<string>(() => {
        return [recipient.department, recipient.email, recipient.phone]
            .filter((p) => p)
            .join(', ');
    }, [recipient]);

    return (
        <ListItem onClick={handleClick}>
            <ListItemIcon>
                <Avatar primary>{abbr}</Avatar>
            </ListItemIcon>
            <ListItemText>
                <ListItemName>{recipient.recipientName}</ListItemName>
                {description &&
                    <ListItemDescription>{description}</ListItemDescription>
                }
            </ListItemText>
            <ListItemIcon>
                {MaterialIcon.chevron_right}
            </ListItemIcon>
        </ListItem>
    );
}

const styles = stylesheet({
    viewContent: {
        backgroundColor: '#fff'
    },
    searchBox: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    results: {
        flexGrow: 1
    },
    control: {
        paddingBottom: 15
    },
    navigation: {
        marginTop: 5
    },
    recipientSelection: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-evenly'
    },
    contentBox: {
        width: '100%'
    },
    promptText: {
        fontSize: '1.25rem',
        textAlign: 'center',
        marginTop: '4.5em'
    },
    textInput: {
        fontWeight: "bold",
        width: "100%",
        height: "4rem",
        fontSize: "1.2rem",
        borderBottom: '1px solid black',
    },
    keyboard: {
        backgroundColor: "transparent",
        fontSize: "0.75rem",
        marginTop: 30
    },
    keyboardButtonContainer: {
        paddingBottom: "1em"
    }
})